import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, AlertTriangle, Calendar, Clock } from 'lucide-react';
import LazyImage from '../../components/LazyImage';

const ArticleVanityMetric = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Resources", url: "/resources" },
    { name: "Why Lab Accuracy is a Vanity Metric", url: "/resources/vanity-metric" }
  ];
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Why Lab Accuracy is a Vanity Metric",
    "description": "Understanding why 99% lab accuracy doesn't guarantee real-world AI performance. Learn about the distribution shift trap, overfitting to silence, and edge case bottlenecks.",
    "datePublished": "2024-12-16",
    "dateModified": "2024-12-16",
    "author": {
      "@type": "Organization",
      "name": "Orboroi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Orboroi",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/resources/vanity-metric`
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Why Lab Accuracy is a Vanity Metric"
        description="Understanding why 99% lab accuracy doesn't guarantee real-world AI performance. Learn about distribution shift, overfitting, and edge case bottlenecks in production AI."
        canonical="/resources/vanity-metric"
        type="article"
        author="Orboroi Team"
        datePublished="2024-12-16"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />
      {/* Back Navigation */}
      <div className="container mx-auto px-6 py-6">
        <Link to="/resources">
          <Button variant="ghost" className="text-[#379F7F] hover:bg-[#379F7F]/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>
        </Link>
      </div>

      {/* Article Hero */}
      <section className="py-8 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-8 h-8 text-[#379F7F]" />
              <span className="text-[#379F7F] font-semibold">Model Evaluation</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Why Lab Accuracy is a Vanity Metric
            </h1>
            
            <div className="flex items-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Dec 16, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>6 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <article className="prose prose-invert prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">The 99% Delusion</h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  You've spent months refining your model. The validation sets are clean, the F1 scores are soaring, 
                  and your dashboard shows a crisp 99% accuracy. In the "lab"—that controlled, sanitized environment 
                  of your initial training data—your AI is a genius.
                </p>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Then you deploy to the real world. Suddenly, your model starts seeing tigers in the suburbs.
                </p>
              </div>

              {/* Tiger vs Dog Phenomenon */}
              <div className="mb-12 bg-gray-900 border-l-4 border-[#379F7F] p-8 rounded-r-lg">
                <h2 className="text-3xl font-bold text-white mb-6">The "Tiger vs. Dog" Phenomenon</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Look at the below image. From a distance, the data points are clear: orange hue, black stripes, 
                  four legs. Without context, the model's "Data Certainty" is high, but its real-world reliability is zero.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  It has failed to account for the fence, the shadows, and the urban environment—factors never properly 
                  weighted in the "clean" training set.
                </p>
              </div>

              {/* Tiger vs Dog Image */}
              <div className="mb-12">
                <div className="relative rounded-xl overflow-hidden">
                  <LazyImage
                    src="/tiger-dog-vanity-metric-new.jpg"
                    alt="AI model misclassifying a dog as a tiger - demonstrating the vanity metric problem with context labels"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                This is more than a glitch; it's a symptom of three systemic production killers:
              </p>

              {/* Killer #1 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-[#379F7F] text-black rounded-full font-bold">1</span>
                  The Distribution Shift Trap
                </h3>
                <p className="text-gray-300 leading-relaxed pl-13">
                  Models often fail because the "In Production" environment looks nothing like the "In Training" environment. 
                  When your training data lacks the messy, multi-modal noise of reality—like harsh shadows or industrial 
                  debris—the model over-indexes on simple patterns.
                </p>
              </div>

              {/* Killer #2 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-[#379F7F] text-black rounded-full font-bold">2</span>
                  Overfitting to Silence
                </h3>
                <p className="text-gray-300 leading-relaxed pl-13">
                  In the lab, data is quiet. In the real world, data is noisy. If your annotation standards were inconsistent 
                  or lacked a managed QA layer during the labeling phase, your model learned to recognize a "Tiger" in a vacuum 
                  rather than a "Dog" in a complex scene.
                </p>
              </div>

              {/* Killer #3 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 bg-[#379F7F] text-black rounded-full font-bold">3</span>
                  The Edge Case Bottleneck
                </h3>
                <p className="text-gray-300 leading-relaxed pl-13">
                  Most AI failures happen at the "edges"—the 1% of scenarios that occur 50% of the time in production. 
                  Without continuous validation pipelines, you aren't just missing these cases; you're scaling your failures.
                </p>
              </div>

              {/* Moving to Production */}
              <div className="mb-12 bg-gradient-to-r from-gray-900 to-black p-8 rounded-lg border border-gray-800">
                <h2 className="text-3xl font-bold text-white mb-6">Moving from Prototype to Production</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Scaling AI safely requires moving past vanity metrics and toward Data Certainty. This means:
                </p>
                
                <div className="space-y-6 pl-6">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#379F7F] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Gold-Standard Dataset Creation</h4>
                      <p className="text-gray-400">Ensuring your ground truth accounts for environmental context.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#379F7F] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Continuous Data QA</h4>
                      <p className="text-gray-400">Implementing a feedback loop that identifies "Tiger Test" failures in real-time.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#379F7F] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Dedicated Data Ops</h4>
                      <p className="text-gray-400">Using embedded teams to catch the nuances that automated benchmarks miss.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orboroi Standard */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">The Orboroi Standard</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  At Orboroi, we help Computer Vision and Applied AI companies bridge the gap between "Lab Accurate" 
                  and "Production Reliable". We provide the high-quality datasets and rigorous validation frameworks 
                  needed to ensure your model doesn't just see patterns, but understands the world.
                </p>
                <p className="text-xl text-white font-semibold">
                  Don't let your model fail the real world. Optimize for certainty, not just scores.
                </p>
              </div>

              {/* CTA Section */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Ready to Eliminate Model Drift and Handle Edge Cases with Confidence?
                </h3>
                <p className="text-gray-300 mb-6">
                  Partner with Orboroi to build production-ready AI systems that perform reliably in the real world.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                    Talk to a Data Expert
                  </Button>
                </Link>
              </div>

            </article>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 bg-black border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-8 text-white">Related Resources</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/resources/concept-drift" className="group">
                <div className="bg-gray-900 p-6 rounded-lg border border-gray-800 hover:border-[#379F7F] transition-all">
                  <h4 className="text-xl font-semibold mb-2 text-white group-hover:text-[#379F7F]">
                    Mitigating Concept Drift
                  </h4>
                  <p className="text-gray-400">Learn how to prevent and detect concept drift in production AI systems.</p>
                </div>
              </Link>
              
              <Link to="/resources/edge-case-management" className="group">
                <div className="bg-gray-900 p-6 rounded-lg border border-gray-800 hover:border-[#379F7F] transition-all">
                  <h4 className="text-xl font-semibold mb-2 text-white group-hover:text-[#379F7F]">
                    Edge Case Management
                  </h4>
                  <p className="text-gray-400">Discover how to identify and handle edge cases that break production models.</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleVanityMetric;
